import { BusinessObjectBase } from './base';
import { termTimeSchema } from '@schemas/business-objects/term-time';
import type { Attendance } from './attendance';
import type { Slot } from './slot';
import type { Term } from './term';
import type { Id, Time } from '../base-types';

export interface TermTimeData {
  readonly id: Id;
  readonly name?: string;
  readonly startTime?: Time;
  readonly endTime?: Time;
  readonly termId: Id;
  readonly term?: Term;
  readonly slots?: Slot[];
  readonly attendances?: Attendance[];
}

export interface TermTime extends Readonly<TermTimeData> {}
type TermTimeCreateWithChildrenData = Partial<Omit<TermTimeData, 'slots'>> & {
  slots?: Partial<Slot>[];
};

export class TermTime extends BusinessObjectBase {
  static readonly entityName = 'TermTime' as const;
  static readonly plural = 'term-times';
  static schema = termTimeSchema;

  // --- Static CRUD methods ---

  static async find(filter?: any): Promise<TermTime[]> {
    const url = `${this.baseUrl}/${this.plural}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const results = await this.get<any[]>(url, params);
    return results.map((data) => new TermTime(data));
  }

  static async findOne(filter?: any): Promise<TermTime | null> {
    const url = `${this.baseUrl}/${this.plural}/find-one`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return data ? new TermTime(data) : null;
  }

  static async findById(id: Id, filter?: any): Promise<TermTime> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return new TermTime(data);
  }

  static async create(data: Omit<TermTimeData, 'id'>): Promise<TermTime> {
    const url = `${this.baseUrl}/${this.plural}`;
    const result = await this.post<any>(url, data);
    return new TermTime(result);
  }

  static async createMany(
    data: Omit<TermTimeData, 'id'>[],
  ): Promise<TermTime[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new TermTime(item));
  }

  static async createWithChildren(
    data: TermTimeCreateWithChildrenData,
  ): Promise<TermTime> {
    const url = `${this.baseUrl}/${this.plural}/create-with-children`;
    const result = await this.post<any>(url, data);
    return new TermTime(result);
  }

  static async createManyWithChildren(
    data: TermTimeCreateWithChildrenData[],
  ): Promise<TermTime[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many-with-children`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new TermTime(item));
  }

  static async updateById(
    id: Id,
    updates: Partial<TermTimeData>,
  ): Promise<TermTime> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const result = await this.patch<any>(url, updates);
    return new TermTime(result);
  }

  static async deleteById(id: Id): Promise<boolean> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    try {
      await this.del<void>(url);
      return true;
    } catch {
      return false;
    }
  }
}
